/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 사용자 페이지 레이아웃
 * 버전 : 1.0
 * 파일명 : UserLayout.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2026-01-09, 강동형 : 위치기반 서비스 이용약관 UI 추가
 * ================================================================ */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CheckIconIMG from 'src/assets/images/CheckIcon.png';
import ArrowBackIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Button as MuiButton } from '@mui/material';
import { useNavbarStore } from 'src/stores/UIStore';
import { getAgreement } from 'src/api/Auth';
import { Icon_X_Mark } from 'src/components/common/CommonIcons';
import { NextButton } from 'src/components/common/CommonStyled';

// styled-components를 사용하여 약관 동의 페이지 컨테이너 스타일을 정의합니다.
const AgreementPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* height: 100vh; */
  background-color: white;
  padding: 0px 20px;
  box-sizing: border-box; // padding과 border를 포함하여 크기를 계산합니다.
  padding-bottom: 6rem; // "다음" 버튼과 겹치지 않도록 충분한 패딩을 추가합니다.
`;

// 페이지 제목 스타일을 정의합니다.
const Title = styled.h1`
  text-align: center;
  font-weight: bold;
  font-size: 18px;
`;

// 설명 텍스트 스타일을 정의합니다.
const Description = styled.p`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 2rem;
  margin-right: auto;
`;

// 약관 항목 컨테이너 스타일을 정의합니다.
const AgreementItemContainer = styled.div<{ $isLargeGap?: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: ${({ $isLargeGap }) => ($isLargeGap ? '2rem' : '0.5rem')}; // 간격을 조정합니다.
  width: 100%;
`;

// 체크 버튼 스타일을 정의합니다.
const CheckButton = styled.button<{ selected: boolean }>`
  min-width: 20px;
  max-width: 20px;
  min-height: 20px;
  max-height: 20px;
  border-radius: 100%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ selected }) => (selected ? ' #39B95B': 'gray')}; // 선택 여부에 따라 배경색을 설정합니다.
  color: white;
  cursor: pointer;
  margin-right: 7px;
`;

// 약관 텍스트 스타일을 정의합니다.
const AgreementText = styled.span<{ $bold?: boolean }>`
  flex: 1;
  font-size: ${({ $bold }) => ($bold ? '1.2rem' : '1rem')}; // bold 속성에 따라 글씨 크기를 설정합니다.
  font-weight: ${({ $bold }) => ($bold ? '700' : '500')}; // bold 속성에 따라 글씨 두께를 설정합니다.
`;

// 약관 설명 텍스트 스타일을 정의합니다.
const AgreementDescription = styled.span`
  font-size: 0.875rem;
  color: gray;
  display: block;
  margin-top: 0.2rem; // 아래 문구와 살짝 간격을 둡니다.
`;

// "보기" 버튼 스타일을 정의합니다.
const ViewButton = styled(MuiButton)`
  font-size: 13px;
  color: gray !important; // 글씨 색상을 회색으로 강제 적용합니다.
  text-transform: none;
  margin-left: auto;
  font-weight: 500;
`;

// "다음" 버튼 스타일을 정의합니다.
const NextButtonContainer = styled.div<{$isView?:boolean}>`
  border-top: ${({$isView})=>($isView ? '1px solid #DDD':'none')};
  position: fixed;
  bottom: 0px; 
  left: 50%;
  transform: translateX(-50%); // 가운데 정렬
  z-index: 1000; // 다른 요소 위에 표시되도록 z-index 설정
  width: 100%;
  font-size: 1rem;
  background-color: white;
  padding: 20px;
  padding-top: 16px;
`;

const CheckIcon = styled.img`

`

// 약관 항목 배열을 정의합니다.
const agreements = [
  { id: 1, text: '[필수] 이용약관 동의' },
  { id: 2, text: '[필수] 개인정보 수집 및 이용 동의' },
  { id: 3, text: '[필수] 위치기반 서비스 이용약관 동의' },
];

const HeaderContainer = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const AgreementHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 11px;

  position: relative;
  font-size: 18px;
  font-weight: 700;

  .icon {
    position: absolute;
    right: 20px;
  }
`

const AgreementWrapper = styled.div`
  font-size: 14px;
  font-weight: 400;
  padding: 20px;
  max-height: calc(100vh - 128px);
  overflow-y: auto;
`

const Line = styled.div<{ $isTitle?: boolean; $isMainTitle?: boolean }>`
  margin-bottom: ${({ $isMainTitle, $isTitle }) => ($isMainTitle ? '20px' : $isTitle ? '0px': '20px')};
  font-weight: ${({ $isMainTitle, $isTitle }) => ($isMainTitle ? '700' : $isTitle ? '600' : '400')};
  font-size: ${({ $isMainTitle }) => ($isMainTitle ? '16px' : '14px')};
  color: black;
`;

// SignUp 컴포넌트를 정의합니다.
const SignUp = () => {
  const [selectedAgreements, setSelectedAgreements] = useState<number[]>([]); // 선택된 약관 항목을 저장하는 상태를 정의합니다.
  const [currentAgreement, setCurrentAgreement] = useState<string>(''); // 현재 선택된 약관을 저장하는 상태를 정의합니다.
  const navigate = useNavigate(); // 페이지 이동을 위한 useNavigate 훅을 사용합니다.
  const setNavField= useNavbarStore((state)=>state.setField)
  const [viewAgree,setViewAgree] = useState(false)
  const [agreeId,setagreeId] = useState(0)

  const lines = currentAgreement.split(/\r\n\r\n|\n\n|\r\r/);
  const titleLine = lines[0];
   useEffect(()=>{
      setNavField('isvisible',false)
        return()=>{
          setNavField('isvisible',true)
        }
    },[])

  // 약관 항목을 토글하는 함수입니다.
  const handleToggleAgreement = (id: number) => {
    if (id === 0) {
      if (selectedAgreements.length === agreements.length) {
        setSelectedAgreements([]); // 모든 약관이 선택된 상태에서 전체 동의를 클릭하면 모두 해제합니다.
      } else {
        setSelectedAgreements(agreements.map((agreement) => agreement.id)); // 모든 약관이 선택되지 않은 상태에서 전체 동의를 클릭하면 모두 선택합니다.
      }
    } else {
      setSelectedAgreements((prev) =>
        prev.includes(id) ? prev.filter((agreementId) => agreementId !== id) : [...prev, id]
      ); // 개별 약관을 토글합니다.
    }
  };

  // 약관 내용을 보기 위한 함수입니다.
  const handleViewAgreement = async (agreementId: number) => {
    const response = await getAgreement()
    setCurrentAgreement(response.data[agreementId].content)
    setagreeId(agreementId+1)
    setViewAgree(true); // 모달을 엽니다.
  };

  const handleAgree = (id: number) => {
    setSelectedAgreements((prev) =>
      prev.includes(id) ? prev : [...prev, id])
    setViewAgree(false); // 모달을 닫습니다.
  };

  // 모달을 닫는 함수입니다.
  const handleCloseAgreement = async () => {
    setViewAgree(false); // 모달을 닫습니다.
  };

  // 모든 약관이 선택되었는지 여부를 확인하는 변수입니다.
  const allAgreementsSelected = selectedAgreements.length === agreements.length;

  return (
    <>
    {viewAgree ?       
      <>
        <AgreementHeader>
          <div className='titleSpan'>{titleLine}</div>
          <Icon_X_Mark className='icon' onClick={handleCloseAgreement}/>
        </AgreementHeader>
        <AgreementWrapper>
          {lines.map((line, idx) => {
            const isTitle = line.startsWith('제') && line.includes('조');
            const isMainTitle = idx === 0;

            // 줄바꿈 <br>, **강조** -> <strong>
            const htmlLine = line
              .replace(/\r\n|\n|\r/g, '<br />')
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // 굵게
              .replace(/\*(.*?)\*/g, '<em>$1</em>'); // 이탤릭

            return (
              <Line
                key={idx}
                $isTitle={isTitle}
                $isMainTitle={isMainTitle}
                dangerouslySetInnerHTML={{
                  __html: htmlLine,
                }}
              />
            );
          })}

        </AgreementWrapper>
        <NextButtonContainer $isView={true}>
          <NextButton
            onClick={() => handleAgree(agreeId)}
          >
            동의
          </NextButton>
        </NextButtonContainer>
      </>
      :
    <AgreementPageContainer>
      <HeaderContainer>
        <Title>약관동의</Title>
        <ArrowBackIcon sx={{
          fontSize:24,
          position: 'absolute',
          display:'flex',
          left: 0
          }}
          onClick={()=>navigate('/')}/>
      </HeaderContainer>
      <Description>국방 홍보 지원 서비스 동의</Description>

      <AgreementItemContainer $isLargeGap>
        <CheckButton selected={allAgreementsSelected} onClick={() => handleToggleAgreement(0)}>
          <CheckIcon src={CheckIconIMG}/>
        </CheckButton>
        <div>
          <AgreementText $bold>전체 동의</AgreementText>
          <AgreementDescription>서비스 이용을 위해 아래 약관에 모두 동의합니다</AgreementDescription>
        </div>
      </AgreementItemContainer>

      {agreements.map((agreement) => (
        <AgreementItemContainer key={agreement.id}>
          <CheckButton
            selected={selectedAgreements.includes(agreement.id)}
            onClick={() => handleToggleAgreement(agreement.id)}
          >
            <CheckIcon src={CheckIconIMG}/>
          </CheckButton>
          <AgreementText>{agreement.text}</AgreementText>
          <ViewButton onClick={() => handleViewAgreement(agreement.id-1)}>보기</ViewButton>
        </AgreementItemContainer>
      ))}

      <NextButtonContainer>
        <NextButton
          disabled={!allAgreementsSelected}
          onClick={() => navigate('/signup-details')}
        >
          동의
        </NextButton>
      </NextButtonContainer>
    </AgreementPageContainer>
    }
    </>
  );
};

export default SignUp;